// BRIEF 117 §8 — THE MATTER, AS A LABEL, IN ONE PLACE.
//
// §8 says: "follow the app's existing matter-label helper if one exists … if a
// shared helper exists, widen it rather than compose a second string."
//
// ── THE MEASUREMENT: THERE IS NO SHARED HELPER ──────────────────────────────
//
// ELEVEN sites compose a matter label, every one of them inline, and every one
// of them REF-FIRST with an em dash:
//
//   CaseView.tsx:1093 · CollectiveCaseView.tsx:3840 · ChatView.tsx:524, :1462
//   InvoiceView.tsx:598 · SearchPopover.tsx:87, :108 · OutlookImportModal.tsx:99
//   CaseTimelineSection.tsx:299 · memoExport.ts:131 · CaseCalculatorSection.tsx:353
//
// The nearest thing to a helper, `caseLabelOf` (CompactColumn.tsx:317), answers
// a different question — `ref || title`, ONE of the two, never both — and is
// local to that file.
//
// So this is a first helper rather than a widened one, and the eleven inline
// composers are named rather than swept: converting them is a different change
// on eleven surfaces, and §8 is about what a new event is called.
//
// ── THE ORDER IS LAURENT'S, THE SEPARATOR IS THE HOUSE'S ────────────────────
//
// ORDER: title first. §8 measured that CaseView's header renders
// `{cs.ref} — {cs.title}`, the opposite, and rules explicitly — "Laurent's
// example rules, title first" ("Johnson vs Bank - 2023/01").
//
// SEPARATOR: §8 asks for the one "actually used" to be enumerated, so: his
// example writes a hyphen and the app writes an EM DASH at all eleven sites
// above. The em dash is taken, because a prefilled title that punctuates itself
// differently from every other matter label in the product is the odd one out,
// and because the choice is one character to reverse if he wants his hyphen.

export type MatterLike = { title?: string | null; ref?: string | null };

/* ── BRIEF 191 §C — THE MATTER RESOLVER, ONE FOR EVERY SITE ─────────────────
   [RULED — carried, 191 C.0] One resolver; every site that resolves a matter
   from the case list alone gets it. A case's own matrix does not repeat the
   case name on every card (that is a host prop, EisenTaskCard `showMatter`).

   THE DEFECT IT CLOSES: fifteen sites read `cases.find(c => c.id === x.caseId)`
   and nothing else, so a collective-case task or event — `ccId` set, `caseId`
   null — resolved NOTHING on the Eisenhower dialog, My Bento, the schedule's
   overlay, the three timer readouts, the bell's five sources, the search
   dialog's four rows and the Dashboard's 14-day strip, while its neighbours
   (AgendaView, ReportsView, the two time-entry modals, NotifRowView, App's
   delegation notices, CompactColumn) already asked both lists.

   THE SHAPE IS THE NEIGHBOURS' MAJORITY, character for character:
       caseId != null ? cases.find(…) : ccId != null ? collectiveCases.find(…) : null
   (App.tsx's `_dcs` / `_acs` / `_pcs`, NotifRowView, BulkTimeEntryModal). A
   site whose expression is exactly that moved onto this function; a site
   whose expression differs — AgendaView's `(case) || (cc)` fallback, the
   two-lookup rows that also need the KIND for navigation, TimeEntryModal's
   case-id-in-either-list legacy — was left where it stands (rule 4, never
   approximate). Each caller passes ITS OWN fields: a task `caseId`/`ccId`, an
   event `caId`/`ccId` (the wire carries both `caseId` and `caId` on an event,
   routes/agenda.js `shape`), a time entry `caId`/`ccId`, a timer subject
   `caseId`/`ccId` (timerSubject reads `parent.caseId`, which the event row
   carries too).

   It answers the ROW, never the kind: a caller that navigates derives the
   kind from the ids it holds (`caId != null ? "ca" : "cc"`), as AgendaView's
   `cs2Kind` does. `null` is a real answer — the matter is not in this
   client's lists (out of scope, not loaded) — and every caller already
   handled exactly that, since `.find` could always miss. */
export interface MatterRef { caseId?: number | null; ccId?: number | null }
/** What every reader of the resolved row asks of it — the name, the ref, the
 *  links feed (LinkIcon). A case row and a collective-case row both carry them;
 *  a host that hands `any` lists gets this shape back, a typed host its own. */
export interface MatterRow { id: number; title?: string; ref?: string; itemLinks?: unknown }

export function resolveMatter(
  ref: MatterRef | null | undefined,
  cases: readonly MatterRow[] | null | undefined,
  collectiveCases: readonly MatterRow[] | null | undefined,
): MatterRow | null {
  if (!ref) return null;
  const caseId = ref.caseId;
  if (caseId != null) return (cases ?? []).find((c) => c.id === caseId) ?? null;
  const ccId = ref.ccId;
  if (ccId != null) return (collectiveCases ?? []).find((c) => c.id === ccId) ?? null;
  return null;
}

/**
 * `"Johnson vs Bank — 2023/01"`. Either half missing gives the other alone;
 * both missing gives `""`, which callers read as "prefill nothing".
 */
export function matterEventTitle(m: MatterLike | null | undefined): string {
  const title = String(m?.title || "").trim();
  const ref = String(m?.ref || "").trim();
  return [title, ref].filter(Boolean).join(" — ");
}

/* ── BRIEF 216 §1 [RULED 70] — THE EVENT'S OWN TITLE BESIDE ITS MATTER ──────
   The Agenda's row reads the matter's title, its ref, then the event's own
   title. A title that only repeats the matter is not shown twice [ADVISOR]:
   the matter's title itself, or 117 §8's old prefill of it — `matterEventTitle`
   above, « title — ref » with the house em dash (0da54dc9; withdrawn in place
   by 191 D.0, App.tsx, but rows it seeded are still stored). Compared trimmed,
   exactly as written: anything else the user typed is theirs and is shown. */
export function eventOwnTitleBeside(
  evTitle: string | null | undefined,
  m: MatterLike | null | undefined,
): string {
  const own = (evTitle ?? "").trim();
  if (!own || !m) return own;
  if (own === (m.title ?? "").trim()) return "";
  if (own === matterEventTitle(m)) return "";
  return own;
}

/** The Agenda row's two readings, narrowed once from the view's untyped rows: the event's own title beside its
 *  matter (`own`, "" when there is no matter or it only repeats it) and the row's tooltip — the matter's title,
 *  its ref and the event's own title, in the row's order; the event's title alone when no matter is visible. */
export function agendaRowTitles(ev: unknown, matter: unknown): { own: string; tip: string } {
  const title = (ev as { title?: string | null } | null)?.title ?? "";
  const m = matter ? (matter as MatterLike) : null;
  if (!m) return { own: "", tip: title };
  const own = eventOwnTitleBeside(title, m);
  return { own, tip: [m.title, m.ref, own].filter(Boolean).join(" — ") };
}

/* ── BRIEF 123 §4 — THE CLIENT FAMILY'S HEADING FOLLOWS THE MATTER ──────────
   [MEASURED at 50fb417c] one route in four was wrong. `CollectiveCaseView`'s
   parties pane passed `t("cc.parties.committee")`; App's `meetingRosterFor`
   hardcoded `t("case.parties.clients")`. So a collective case opened from the
   parties pane said COMMITTEE and the same case opened from the Events list
   said CLIENTS — for the same people, on the same matter.

   A FUNCTION AND NOT A CONVENTION, because a convention is what let them
   disagree. Every builder asks this, so the two cannot answer differently
   again: there is only one answer to ask for.

   The key alone, never the resolved word: each builder holds its own `t`. */
export function clientsFamilyKey(isCollective: boolean): string {
  return isCollective ? "cc.parties.committee" : "case.parties.clients";
}
